import { ref } from "vue";

/**
 * Production-grade passkey support detection.
 * - Secure context check
 * - WebAuthn API presence
 * - Platform authenticator availability (UVPA)
 * - Optional conditional mediation check (not required for visibility)
 * - Result cached in-memory to avoid repeated async calls
 */
export const passkeySupport = (() => {
  const supportedRef = ref<boolean | null>(null);
  let inFlight: Promise<boolean> | null = null;

  const isEnabled = (): boolean => {
    try {
      return import.meta.env.VITE_ENABLE_PASSKEY === "true";
    } catch {
      return false;
    }
  };

  const isFeatureAvailableSync = (): boolean => {
    return (
      typeof window !== "undefined" &&
      window.isSecureContext === true &&
      typeof (window as any).PublicKeyCredential === "function" &&
      typeof navigator !== "undefined" &&
      !!navigator.credentials
    );
  };

  const withTimeout = async <T>(
    promise: Promise<T>,
    ms: number,
    fallback: T,
  ): Promise<T> => {
    let timeoutId: number | undefined;
    const timeoutPromise = new Promise<T>((resolve) => {
      timeoutId = window.setTimeout(() => resolve(fallback), ms);
    });
    const result = await Promise.race([
      promise
        .then((v) => v)
        .catch(() => fallback),
      timeoutPromise,
    ]);
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
    return result;
  };

  const detectSupport = async (): Promise<boolean> => {
    if (supportedRef.value !== null) return supportedRef.value;

    if (!isFeatureAvailableSync()) {
      supportedRef.value = false;
      return false;
    }

    if (inFlight) return inFlight;

    const detection = (async () => {
      // Platform authenticator availability (primary signal)
      const uvpaPromise = ((window as any).PublicKeyCredential?.
        isUserVerifyingPlatformAuthenticatorAvailable?.() as
        | Promise<boolean>
        | undefined) ?? Promise.resolve(false);

      const platformAvailable = await withTimeout(uvpaPromise, 1500, false);

      // Conditional Mediation (optional, informative)
      const cmaPromise = ((window as any).PublicKeyCredential?.
        isConditionalMediationAvailable?.() as
        | Promise<boolean>
        | undefined) ?? Promise.resolve(false);
      await withTimeout(cmaPromise, 1000, false);

      const supported = !!platformAvailable;
      supportedRef.value = supported;
      return supported;
    })().finally(() => {
      inFlight = null;
    });

    inFlight = detection;
    return detection;
  };

  return {
    /** Feature flag (env) */
    isEnabled,
    /** Cheap sync capability check */
    isFeatureAvailableSync,
    /** Async robust support detection with caching */
    detectSupport,
    /** Reactive cached result: true/false/null (unknown/not checked) */
    supportedRef,
  } as const;
})();

export type PasskeySupportService = typeof passkeySupport;


